import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { warehouseAPI } from '../../api/warehouses';
import { toast } from 'react-hot-toast';

interface ReconciliationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  warehouseId: number;
  itemId: number;
  itemName: string;
  warehouseName: string;
  systemStock: number;
  unit: string;
}

type ReconciliationReason = 'DATA_ENTRY_ERROR' | 'NATURAL_LOSS' | 'THEFT_DAMAGE' | 'OTHER' | '';

export const ReconciliationModal: React.FC<ReconciliationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  warehouseId,
  itemId,
  itemName,
  warehouseName,
  systemStock,
  unit,
}) => {
  const { t } = useTranslation();
  const [actualCount, setActualCount] = useState<string>('');
  const [reason, setReason] = useState<ReconciliationReason>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const difference = actualCount ? parseFloat(actualCount) - systemStock : 0;
  const hasDifference = difference !== 0;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setActualCount(systemStock.toString());
      setReason('');
      setNotes('');
      setShowConfirm(false);
    }
  }, [isOpen, systemStock]);

  const formatUnit = (u: string) => {
    const unitMap: Record<string, string> = {
      M3: 'm³',
      TON: 'tấn',
      KG: 'kg',
      PIECE: 'cái',
    };
    return unitMap[u] || u;
  };

  const isValid = () => {
    const count = parseFloat(actualCount);
    if (isNaN(count) || count < 0) return false;
    if (hasDifference && !reason) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    try {
      setSubmitting(true);
      await warehouseAPI.reconcile(warehouseId, itemId, {
        actualCount: parseFloat(actualCount),
        reason: reason || undefined,
        notes: notes || undefined,
      });

      toast.success(t('warehouses.reconciliation.success') || 'Cân đối tồn kho thành công');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Reconciliation error:', error);
      toast.error(
        error.response?.data?.message ||
          t('warehouses.reconciliation.error') ||
          'Không thể cân đối tồn kho'
      );
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  };

  const handleConfirm = () => {
    if (!isValid()) return;
    setShowConfirm(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {t('warehouses.reconciliation.title') || 'Cân đối tồn kho'}: {itemName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={submitting}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Warehouse Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Kho:</span> {warehouseName}
            </div>
          </div>

          {/* System Stock */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">
              {t('warehouses.reconciliation.systemStock') || 'Tồn kho hệ thống'}
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {systemStock} {formatUnit(unit)}
            </div>
          </div>

          {/* Actual Count Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('warehouses.reconciliation.actualCount') || 'Thực tế cân/đếm'} *
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.001"
                value={actualCount}
                onChange={(e) => setActualCount(e.target.value)}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={submitting}
              />
              <span className="text-gray-600">{formatUnit(unit)}</span>
            </div>
          </div>

          {/* Difference Display */}
          {actualCount && (
            <div
              className={`rounded-lg p-4 ${
                difference === 0
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-yellow-50 border border-yellow-200'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                {difference === 0 ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                )}
                <div className="text-sm font-medium">
                  {t('warehouses.reconciliation.difference') || 'Chênh lệch'}:
                </div>
              </div>
              <div
                className={`text-2xl font-bold ${
                  difference > 0
                    ? 'text-green-600'
                    : difference < 0
                    ? 'text-red-600'
                    : 'text-gray-600'
                }`}
              >
                {difference > 0 && '+'}
                {difference.toFixed(3)} {formatUnit(unit)}
              </div>
              {difference !== 0 && (
                <div className="text-sm text-gray-600 mt-1">
                  {difference > 0
                    ? t('warehouses.reconciliation.moreActual') || 'Thực tế nhiều hơn hệ thống'
                    : t('warehouses.reconciliation.lessActual') || 'Thực tế ít hơn hệ thống'}
                </div>
              )}
              {difference === 0 && (
                <div className="text-sm text-green-600 mt-1">
                  {t('warehouses.reconciliation.noDifference') || 'Không chênh lệch'}
                </div>
              )}
            </div>
          )}

          {/* Reason (required if difference != 0) */}
          {hasDifference && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <div className="font-medium text-gray-900 mb-2">
                    {t('warehouses.reconciliation.reason') || 'Lý do chênh lệch'} *
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reason"
                        value="DATA_ENTRY_ERROR"
                        checked={reason === 'DATA_ENTRY_ERROR'}
                        onChange={(e) => setReason(e.target.value as ReconciliationReason)}
                        className="text-blue-600 focus:ring-blue-500"
                        disabled={submitting}
                      />
                      <span>
                        {t('warehouses.reconciliation.dataEntryError') || 'Sai sót nhập liệu'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reason"
                        value="NATURAL_LOSS"
                        checked={reason === 'NATURAL_LOSS'}
                        onChange={(e) => setReason(e.target.value as ReconciliationReason)}
                        className="text-blue-600 focus:ring-blue-500"
                        disabled={submitting}
                      />
                      <span>
                        {t('warehouses.reconciliation.naturalLoss') || 'Hao hụt tự nhiên'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reason"
                        value="THEFT_DAMAGE"
                        checked={reason === 'THEFT_DAMAGE'}
                        onChange={(e) => setReason(e.target.value as ReconciliationReason)}
                        className="text-blue-600 focus:ring-blue-500"
                        disabled={submitting}
                      />
                      <span>
                        {t('warehouses.reconciliation.theftDamage') || 'Thất thoát/hư hỏng'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reason"
                        value="OTHER"
                        checked={reason === 'OTHER'}
                        onChange={(e) => setReason(e.target.value as ReconciliationReason)}
                        className="text-blue-600 focus:ring-blue-500"
                        disabled={submitting}
                      />
                      <span>{t('warehouses.reconciliation.other') || 'Khác'}</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('warehouses.reconciliation.notes') || 'Ghi chú'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Mô tả chi tiết lý do chênh lệch..."
                  disabled={submitting}
                />
              </div>
            </div>
          )}

          {/* Current User & Time Info */}
          <div className="text-sm text-gray-500 space-y-1 bg-gray-50 rounded p-3">
            <div>
              <span className="font-medium">
                {t('warehouses.reconciliation.reconciledAt') || 'Thời gian'}:
              </span>{' '}
              {new Date().toLocaleString('vi-VN')}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            disabled={submitting}
          >
            {t('warehouses.reconciliation.cancel') || 'Hủy'}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid() || submitting}
            className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {submitting
              ? 'Đang xử lý...'
              : t('warehouses.reconciliation.confirm') || 'Xác nhận điều chỉnh'}
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Xác nhận điều chỉnh</h3>
                <p className="text-gray-600">
                  {t('warehouses.reconciliation.confirmMessage') ||
                    'Thao tác này sẽ được ghi lại và không thể hoàn tác. Bạn có chắc chắn?'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                disabled={submitting}
              >
                Hủy
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-gray-300"
                disabled={submitting}
              >
                {submitting ? 'Đang xử lý...' : 'Xác nhận'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
